import React from 'react';
import '../styles/AboutUsPage.css';

const AboutUsPage: React.FC = () => {
  // Team member data with updated, professional photos
  const teamMembers = [
    {
      name: "Sarah Johnson",
      role: "Founder & CEO",
      bio: "A former veterinary technician with 15+ years of experience, Sarah founded Paws & Hearts to create a better future for homeless pets.",
      photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=400&q=80"
    },
    {
      name: "Michael Chen",
      role: "Director of Operations",
      bio: "Michael ensures our operations run smoothly, from managing shelter partnerships to optimizing our adoption process for families.",
      photo: "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=400&q=80"
    },
    {
      name: "Dr. Emma Rodriguez, DVM",
      role: "Lead Veterinarian",
      bio: "Dr. Rodriguez leads our medical team, specializing in the rehabilitation and care of rescue animals to ensure they are healthy for adoption.",
      photo: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=400&q=80"
    },
    {
      name: "David Wilson",
      role: "Community Outreach Lead",
      bio: "David is the face of our community efforts, organizing nationwide adoption events and educational programs to promote animal welfare.",
      photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=400&q=80"
    }
  ];

  // Core values data
  const coreValues = [
    {
      title: "Compassionate Care",
      icon: "❤️",
      description: "Every animal receives unwavering love, respect, and the highest standard of medical attention from the moment they enter our care."
    },
    {
      title: "Responsible Adoption",
      icon: "🏡",
      description: "Our thorough matching process ensures we create perfect, lasting bonds between pets and their new, loving families."
    },
    {
      title: "Community Focus",
      icon: "👨‍👩‍👧‍👦",
      description: "We build and support a network of shelters, volunteers, and advocates to amplify our impact and promote animal welfare."
    },
    {
      title: "Lifelong Support",
      icon: "🤝",
      description: "Our commitment doesn't end at adoption. We provide ongoing resources and support for all our pets and their families."
    }
  ];

  return (
    <div className="about-container">
      {/* Modern Full-Width Hero Section (Unchanged as requested) */}
      <br/><br/><br/><br/>
      <section className="hero-section">
        <div className="hero-overlay">
          <div className="hero-content">
            <div className="hero-text">
              <div className="pre-title">Creating Forever Homes</div>
              <h1>Where Tails Begin & Hearts Connect</h1>
              <p className="hero-subtitle">
                Since 2015, we've helped over 15,000 pets find their forever homes through
                our nationwide adoption network.
              </p>
            </div>
            <div className="hero-stats">
              {[
                { value: "15K+", label: "Pets Adopted" },
                { value: "200+", label: "Shelter Partners" },
                { value: "98%", label: "Adoption Success" }
              ].map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="content-section">
        <div className="section-header">
          <div className="section-subtitle">Our Purpose</div>
          <h2>A World Where Every Pet is Cherished</h2>
          <div className="divider"></div>
        </div>
        <div className="mission-content">
          <div className="mission-image">
            <img src="https://images.unsplash.com/photo-1543466835-00a7907e9de1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80" alt="Happy dog with owner" />
          </div>
          <div className="mission-text">
            <h3>We believe every pet deserves a loving home.</h3>
            <p>
              At Paws & Hearts, our mission is to end pet homelessness. We use technology to connect rescue animals with caring families and provide a support system that ensures each adoption is a success story. We're more than a platform; we're a community dedicated to creating forever bonds.
            </p>
            <ul className="mission-list">
              <li>Comprehensive health and behavioral screenings for every pet.</li>
              <li>A personalized matching process to find the perfect companion.</li>
              <li>A 30-day adoption trial to ensure a seamless transition.</li>
              <li>A lifetime of post-adoption support and resources.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="content-section bg-light">
        <div className="section-header">
          <div className="section-subtitle">Our Journey</div>
          <h2>Milestones & Growth</h2>
          <div className="divider"></div>
        </div>
        <div className="timeline">
          {[
            {
              year: "2015",
              title: "Humble Beginnings",
              content: "Founded in a small Portland garage, our journey began by rescuing 7 abandoned puppies. Our first event found homes for 23 pets."
            },
            {
              year: "2018",
              title: "National Expansion",
              content: "We launched our digital platform and partnered with 50 shelters across 10 states, revolutionizing virtual adoptions."
            },
            {
              year: "2023",
              title: "Making History",
              content: "Awarded the 'Animal Welfare Innovator' prize by the ASPCA after surpassing 15,000 adoptions nationwide."
            }
          ].map((item, index) => (
            <div key={index} className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <div className="timeline-year">{item.year}</div>
                <h3>{item.title}</h3>
                <p>{item.content}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Core Values Section */}
      <section className="content-section">
        <div className="section-header">
          <div className="section-subtitle">Our Foundation</div>
          <h2>The Principles That Guide Us</h2>
          <div className="divider"></div>
        </div>
        <div className="values-grid">
          {coreValues.map((value, index) => (
            <div key={index} className="value-card">
              <div className="value-icon">{value.icon}</div>
              <h3>{value.title}</h3>
              <p>{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team Section */}
      <section className="content-section bg-light">
        <div className="section-header">
          <div className="section-subtitle">Our Leadership</div>
          <h2>Meet The Pack Leaders</h2>
          <div className="divider"></div>
        </div>
        <div className="team-grid">
          {teamMembers.map((member, index) => (
            <div key={index} className="team-card">
              <div className="team-photo">
                <img src={member.photo} alt={member.name} />
              </div>
              <div className="team-info">
                <h3>{member.name}</h3>
                <p className="role">{member.role}</p>
                <p className="bio">{member.bio}</p>
                 <div className="social-links">
                    <a href="#linkedin" className="social-icon" aria-label={`${member.name} LinkedIn`}>
                      <svg viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                    </a>
                    <a href="#twitter" className="social-icon" aria-label={`${member.name} Twitter`}>
                      <svg viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                    </a>
                  </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Your New Best Friend is Waiting</h2>
          <p>
            Take the first step on an incredible journey. Browse our available pets
            and let us help you find the perfect companion to complete your family.
          </p>
          <button className="cta-button">Browse Adoptable Pets</button>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;